// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { TimelineAxisDensity } from '@/components/timeline-axis';
import {
  KeyboardArrowLeftRounded,
  KeyboardArrowRightRounded,
  KeyboardDoubleArrowLeftRounded,
  KeyboardDoubleArrowRightRounded,
  ZoomInRounded,
  ZoomOutRounded,
} from '@/constants/hardfin-standard-icons';
import { pluralize } from '@/utilities/pluralize';
import { Divider, IconButton, Stack, Tooltip } from '@mui/material';

const DENSITY_LABELS: Record<TimelineAxisDensity, string> = {
  day: 'day',
  week: 'week',
  month: 'month',
};

interface Props {
  /**
   * The density of the timeline axis.
   */
  density: TimelineAxisDensity;

  canZoomIn?: boolean;

  canZoomOut?: boolean;

  onClickZoomIn: () => void;

  onClickZoomOut: () => void;

  /**
   * A callback fired when the user clicks a forward button.
   */
  onClickForward: (count: number) => void;

  /**
   * A callback fired when the user clicks a backward button.
   */
  onClickBackward: (count: number) => void;
}

/**
 * A pagination control for navigating a timeline.
 */
export function TimelineControls(props: Props) {
  const getTooltipTitle = (count: number, isForward: boolean) => {
    const label = pluralize(count, { singular: DENSITY_LABELS[props.density] });

    return isForward ? `Forward ${label}` : `Backward ${label}`;
  };

  const createBackwardClickHandler = (count: number) => () => {
    props.onClickBackward(count);
  };

  const createForwardClickHandler = (count: number) => () => {
    props.onClickForward(count);
  };

  return (
    <Stack direction="row">
      <Tooltip arrow placement="top" title="Zoom out">
        <span>
          <IconButton
            size="small"
            disabled={!props.canZoomOut}
            onClick={props.onClickZoomOut}
          >
            <ZoomOutRounded />
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip arrow placement="top" title="Zoom in">
        <span>
          <IconButton
            size="small"
            disabled={!props.canZoomIn}
            onClick={props.onClickZoomIn}
          >
            <ZoomInRounded />
          </IconButton>
        </span>
      </Tooltip>
      <Divider flexItem orientation="vertical" sx={{ marginX: 1 }} />
      <Tooltip arrow placement="top" title={getTooltipTitle(12, false)}>
        <IconButton size="small" onClick={createBackwardClickHandler(12)}>
          <KeyboardDoubleArrowLeftRounded />
        </IconButton>
      </Tooltip>
      <Tooltip arrow placement="top" title={getTooltipTitle(1, false)}>
        <IconButton size="small" onClick={createBackwardClickHandler(1)}>
          <KeyboardArrowLeftRounded />
        </IconButton>
      </Tooltip>
      <Tooltip arrow placement="top" title={getTooltipTitle(1, true)}>
        <IconButton size="small" onClick={createForwardClickHandler(1)}>
          <KeyboardArrowRightRounded />
        </IconButton>
      </Tooltip>
      <Tooltip arrow placement="top" title={getTooltipTitle(12, true)}>
        <IconButton size="small" onClick={createForwardClickHandler(12)}>
          <KeyboardDoubleArrowRightRounded />
        </IconButton>
      </Tooltip>
    </Stack>
  );
}
